import { useState } from "react";
import { Video, Camera, TrendingUp, Users, Sparkles, Target, BarChart3, Lightbulb, Zap, Heart } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { servicesData } from "@/data/servicesData";

interface Service {
  title: string;
  subtitle: string;
  description: string;
  icon: any;
  category: string;
}

// Parse services from servicesData
const parseServices = (): Service[] => {
  const rawServices = servicesData["social-media"];
  const categories = ["Strategie", "Content", "Community", "Analytics", "Beratung"];
  
  return rawServices.map((service, index) => {
    const [title = '', ...descParts] = service.split('–');
    const description = descParts.join('–').trim();
    
    // Assign icons and categories based on keywords
    let icon = Sparkles;
    let category = "Allgemein";
    
    const lowerTitle = title.toLowerCase();
    
    if (lowerTitle.includes('strategie') || lowerTitle.includes('analyse') || lowerTitle.includes('planung')) {
      icon = Target;
      category = "Strategie";
    } else if (lowerTitle.includes('content') || lowerTitle.includes('video') || lowerTitle.includes('foto') || lowerTitle.includes('reel')) {
      icon = Video;
      category = "Content";
    } else if (lowerTitle.includes('community') || lowerTitle.includes('management') || lowerTitle.includes('betreuung')) {
      icon = Users;
      category = "Community";
    } else if (lowerTitle.includes('performance') || lowerTitle.includes('reporting') || lowerTitle.includes('analytics')) {
      icon = BarChart3;
      category = "Analytics";
    } else if (lowerTitle.includes('beratung') || lowerTitle.includes('workshop') || lowerTitle.includes('schulung')) {
      icon = Lightbulb;
      category = "Beratung";
    } else if (lowerTitle.includes('kampagne') || lowerTitle.includes('ads') || lowerTitle.includes('werbung')) {
      icon = TrendingUp;
      category = "Marketing";
    }
    
    return {
      title: title.trim(),
      subtitle: description.slice(0, 60) + (description.length > 60 ? '...' : ''),
      description: description || "Professionelle Social Media Unterstützung für Ihr Unternehmen.",
      icon,
      category
    };
  });
};

const services = parseServices();
const categories = ["Alle", ...Array.from(new Set(services.map(s => s.category)))];

export const SocialMediaServicesGrid = () => {
  const [selectedCategory, setSelectedCategory] = useState("Alle");
  const [selectedService, setSelectedService] = useState<Service | null>(null);

  const filteredServices = selectedCategory === "Alle" 
    ? services 
    : services.filter(s => s.category === selectedCategory);

  return (
    <>
      {/* Category Filter */}
      <div className="flex flex-wrap gap-2 justify-center mb-12">
        {categories.map((category) => (
          <Badge
            key={category}
            variant={selectedCategory === category ? "default" : "outline"}
            className="cursor-pointer px-4 py-2 text-sm hover:scale-105 transition-all"
            onClick={() => setSelectedCategory(category)}
          >
            {category}
          </Badge>
        ))}
      </div>

      {/* Services Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredServices.map((service, index) => {
          const ServiceIcon = service.icon;
          
          return (
            <Card
              key={index}
              className="group relative overflow-hidden cursor-pointer border-border/50 hover:border-primary/50 transition-all duration-300 hover:shadow-2xl hover:shadow-primary/20 hover:-translate-y-2"
              onClick={() => setSelectedService(service)}
            >
              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              
              {/* Content */}
              <div className="relative p-6 space-y-4">
                {/* Icon & Category Badge */}
                <div className="flex items-start justify-between">
                  <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 group-hover:scale-110 transition-all duration-300">
                    <ServiceIcon className="w-7 h-7 text-primary" />
                  </div>
                  <Badge variant="secondary" className="text-xs">
                    {service.category}
                  </Badge>
                </div>

                {/* Title */}
                <h3 className="text-xl font-bold leading-tight group-hover:text-primary transition-colors">
                  {service.title}
                </h3>

                {/* Subtitle */}
                <p className="text-sm text-primary/80 font-medium">
                  {service.subtitle}
                </p>

                {/* Description Preview */}
                <p className="text-sm text-muted-foreground line-clamp-2">
                  {service.description}
                </p>

                {/* Hover indicator */}
                <div className="flex items-center gap-2 text-xs text-primary opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span>Mehr erfahren</span>
                  <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Service Details Dialog */}
      <Dialog open={!!selectedService} onOpenChange={() => setSelectedService(null)}>
        <DialogContent className="max-w-2xl">
          {selectedService && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center">
                    <selectedService.icon className="w-8 h-8 text-primary" />
                  </div>
                  <div className="flex-1">
                    <DialogTitle className="text-2xl mb-1">
                      {selectedService.title}
                    </DialogTitle>
                    <Badge variant="secondary">{selectedService.category}</Badge>
                  </div>
                </div>
              </DialogHeader>
              
              <DialogDescription className="space-y-4">
                <p className="text-base text-foreground leading-relaxed">
                  {selectedService.description}
                </p>
              </DialogDescription>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
